import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, Check, Copy, ExternalLink, X } from 'lucide-react';
import { Badge } from '~/components/ui/badge';
import { familyLabel } from '~/utils/familyLabel';
import { Button } from '~/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '~/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import { EnrollmentStatusBadge } from '~/components/enrollment';
import { useQueueFamilyForInvite } from './useQueueFamilyForInvite';
import { useUnqueueFamily } from './useUnqueueFamily';
import { useSendFamilyInvites } from './useSendFamilyInvites';
import { useResendFamilyInvite } from '~/pages/FamilyDetail/useResendFamilyInvite';
import { useRevokeReenrollmentInvite } from '~/pages/FamilyDetail/useRevokeReenrollmentInvite';
import { useCompleteEnrollmentReview } from '~/pages/FamilyDetail/useCompleteEnrollmentReview';
import { useDeclineFamilyEnrollment } from '~/pages/FamilyDetail/useDeclineFamilyEnrollment';
import { useSendFormBackToFamily } from '~/pages/FamilyDetail/useSendFormBackToFamily';
import { useUndoEnrollmentReview } from '~/pages/FamilyDetail/useUndoEnrollmentReview';
import { useSendEnrollmentPacket } from '~/pages/FamilyDetail/useSendEnrollmentPacket';
import { useRevokeEnrollmentPacket } from '~/pages/FamilyDetail/useRevokeEnrollmentPacket';
import type { FamilyEnrollmentRow } from './types';
import {
  PAYMENT_SCHEDULE_LABELS,
  activityFor,
  describeEnrollment,
  formatMoney,
  formatShortDate,
  isNewStyle,
  phaseFor,
  phasesFor,
  summarizeInvoices,
  type Phase,
} from './enrollmentPhases';

const KIND_LABELS: Record<string, string> = {
  RE_ENROLLMENT: 'Re-enroll',
  NEW_ADMISSION: 'New',
};

const DISPOSITIONS = [
  { value: 'accept', label: 'Accept' },
  { value: 'waitlist', label: 'Waitlist' },
  { value: 'deny', label: 'Deny' },
  { value: 'not_returning', label: 'Not returning' },
];

type Confirmation = 'decline' | 'send_back' | 'revoke_invite' | 'revoke_packet' | 'undo_review' | null;

const CONFIRMATIONS: Record<Exclude<Confirmation, null>, { title: string; body: string; action: string; destructive?: boolean }> = {
  decline: {
    title: 'Decline this enrollment?',
    body: 'Every student on it will be marked denied. The family keeps their records; you can start a new enrollment later.',
    action: 'Decline',
    destructive: true,
  },
  send_back: {
    title: 'Send the form back to the family?',
    body: 'The family can edit and resubmit their form. Anything you have drafted for this review is discarded.',
    action: 'Send back',
  },
  revoke_invite: {
    title: 'Revoke this invite?',
    body: 'The link stops working and the family goes back to the queue.',
    action: 'Revoke invite',
    destructive: true,
  },
  revoke_packet: {
    title: 'Revoke this packet?',
    body: 'The packet link stops working and the enrollment returns to approved so you can resend it.',
    action: 'Revoke packet',
    destructive: true,
  },
  undo_review: {
    title: 'Reopen this review?',
    body: 'Draft invoices are deleted and the enrollment returns to needs review. Not possible once an invoice is posted.',
    action: 'Reopen review',
  },
};

interface FamilyEnrollmentDrawerProps {
  enrollment: FamilyEnrollmentRow | null;
  schoolSlug: string;
  schoolYear: { id: string; name: string; reenrollmentOpen?: boolean | null; openEnrollmentOpen?: boolean | null };
  /**
   * With the checklist on, the year is placed and described by its access, and
   * only Decline is left of the old flow's actions.
   */
  checklistEnrollmentOn?: boolean;
  onClose: () => void;
  onChanged: () => void;
  onNotice: (message: string, tone?: 'success' | 'error') => void;
}

/** Reads the first user error out of any of the enrollment mutation payloads. */
function firstUserError(payload: unknown): string | null {
  const errors = (payload as { userErrors?: Array<{ message: string }> } | null | undefined)?.userErrors;
  return errors && errors.length > 0 ? errors[0].message : null;
}

/**
 * Everything about one family's enrollment this year, in a side panel over the
 * board: where they are, why, the students and their decisions, money,
 * guardians, and the one or two actions that move them forward.
 */
export function FamilyEnrollmentDrawer({
  enrollment,
  schoolSlug,
  schoolYear,
  checklistEnrollmentOn = false,
  onClose,
  onChanged,
  onNotice,
}: FamilyEnrollmentDrawerProps) {
  const [dispositions, setDispositions] = useState<Record<string, string>>({});
  const [confirmation, setConfirmation] = useState<Confirmation>(null);
  const [copied, setCopied] = useState(false);

  const [queueFamily, { loading: queueing }] = useQueueFamilyForInvite();
  const [unqueueFamily, { loading: unqueueing }] = useUnqueueFamily();
  const [sendFamilyInvites, { loading: sendingInvite }] = useSendFamilyInvites();
  const [resendFamilyInvite, { loading: resending }] = useResendFamilyInvite();
  const [revokeInvite, { loading: revokingInvite }] = useRevokeReenrollmentInvite();
  const [completeReview, { loading: completing }] = useCompleteEnrollmentReview();
  const [declineEnrollment, { loading: declining }] = useDeclineFamilyEnrollment();
  const [sendFormBack, { loading: sendingBack }] = useSendFormBackToFamily();
  const [undoReview, { loading: undoing }] = useUndoEnrollmentReview();
  const [sendPacket, { loading: sendingPacket }] = useSendEnrollmentPacket();
  const [revokePacket, { loading: revokingPacket }] = useRevokeEnrollmentPacket();

  const busy = queueing || unqueueing || sendingInvite || resending || revokingInvite || completing ||
    declining || sendingBack || undoing || sendingPacket || revokingPacket;

  const open = enrollment !== null;

  // Default every student to "accept" (or "not returning" when the family said
  // so) each time a different enrollment opens.
  useEffect(() => {
    if (!enrollment) return;
    const initial: Record<string, string> = {};
    for (const se of enrollment.studentEnrollments ?? []) {
      initial[se.id] = se.state === 'NOT_RETURNING' ? 'not_returning' : 'accept';
    }
    setDispositions(initial);
    setCopied(false);
  }, [enrollment?.id, enrollment]);

  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !confirmation) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, confirmation, onClose]);

  // With the checklist on, the header reads the year as the board card does: by its access.
  const status = useMemo(
    () => (enrollment ? describeEnrollment(enrollment, schoolYear, undefined, checklistEnrollmentOn) : null),
    [enrollment, schoolYear, checklistEnrollmentOn]
  );
  const invoices = useMemo(() => summarizeInvoices(enrollment?.invoices ?? []), [enrollment?.invoices]);
  const activity = useMemo(() => (enrollment ? activityFor(enrollment) : []), [enrollment]);
  const currentPhase: Phase | null = enrollment ? phaseFor(enrollment, checklistEnrollmentOn) : null;

  const finish = useCallback(
    (payload: unknown, success: string) => {
      const error = firstUserError(payload);
      if (error) {
        onNotice(error, 'error');
        return;
      }
      onNotice(success, 'success');
      onChanged();
    },
    [onChanged, onNotice]
  );

  if (!enrollment || !status || !currentPhase) return null;

  const family = enrollment.family;
  const familyPath = `/app/${schoolSlug}/families/${family.slug}`;
  const familyDisplay = familyLabel(family.name);
  const portalPath = enrollment.state === 'PACKET_SENT' || enrollment.state === 'PACKET_VIEWED'
    ? `/app/packet/${enrollment.inviteToken}`
    : enrollment.enrollmentKind === 'RE_ENROLLMENT'
      ? `/app/enroll/${enrollment.inviteToken}`
      : `/app/apply/${enrollment.inviteToken}`;
  const students = enrollment.studentEnrollments ?? [];
  const guardians = family.guardians ?? [];
  const reviewReasons = enrollment.reviewReasons ?? [];
  const activeStudents = students.filter((se) => !['WITHDRAWN', 'DENIED'].includes(se.state));
  const acceptingCount = Object.values(dispositions).filter((action) => action === 'accept').length;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}${portalPath}`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      onNotice('Could not copy the link — copy it from the address bar instead.', 'error');
    }
  };

  const runConfirmed = () => {
    const id = enrollment.id;
    const current = confirmation;
    setConfirmation(null);
    switch (current) {
      case 'decline':
        declineEnrollment({
          variables: { familyEnrollmentId: id },
          onCompleted: (r) => finish(r.declineFamilyEnrollment, `Declined the ${familyDisplay}.`),
        });
        break;
      case 'send_back':
        sendFormBack({
          variables: { familyEnrollmentId: id },
          onCompleted: (r) => finish(r.sendFormBackToFamily, `Sent the form back to the ${familyDisplay}.`),
        });
        break;
      case 'revoke_invite':
        revokeInvite({
          variables: { familyEnrollmentId: id },
          onCompleted: (r) => finish(r.revokeReenrollmentInvite, `Revoked the invite for the ${familyDisplay}.`),
        });
        break;
      case 'revoke_packet':
        revokePacket({
          variables: { familyEnrollmentId: id },
          onCompleted: (r) => finish(r.revokeEnrollmentPacket, `Revoked the packet for the ${familyDisplay}.`),
        });
        break;
      case 'undo_review':
        undoReview({
          variables: { familyEnrollmentId: id },
          onCompleted: (r) => finish(r.undoEnrollmentReview, `Reopened the review for the ${familyDisplay}.`),
        });
        break;
      default:
        break;
    }
  };

  const handleQueue = () =>
    queueFamily({
      variables: { familyEnrollmentId: enrollment.id },
      onCompleted: (r) => finish(r.queueFamilyForInvite, `Queued the ${familyDisplay} for an invite.`),
    });

  const handleUnqueue = () =>
    unqueueFamily({
      variables: { familyEnrollmentId: enrollment.id },
      onCompleted: (r) => finish(r.unqueueFamily, `Removed the ${familyDisplay} from the invite queue.`),
    });

  const handleSendInvite = () =>
    sendFamilyInvites({
      variables: { familyEnrollmentIds: [enrollment.id] },
      onCompleted: (r) => {
        const sent = r.sendFamilyInvites?.sentCount ?? 0;
        if (sent > 0) finish(r.sendFamilyInvites, `Invite sent to the ${familyDisplay}.`);
        else onNotice('The invite could not be sent. Check that the enrollment window is open.', 'error');
      },
    });

  const handleResendInvite = () =>
    resendFamilyInvite({
      variables: { input: { familyEnrollmentId: enrollment.id } },
      onCompleted: (r) => finish(r.resendFamilyInvite, `Resent the invite to the ${familyDisplay}.`),
    });

  const handleCompleteReview = () =>
    completeReview({
      variables: {
        input: {
          familyEnrollmentId: enrollment.id,
          studentDispositions: students.map((se) => ({
            studentEnrollmentId: se.id,
            action: dispositions[se.id] ?? 'accept',
          })),
        },
      },
      onCompleted: (r) => finish(r.completeEnrollmentReview, `Review completed for the ${familyDisplay}. Send the packet when you are ready.`),
    });

  const handleSendPacket = () =>
    sendPacket({
      variables: { familyEnrollmentId: enrollment.id },
      onCompleted: (r) => finish(r.sendEnrollmentPacket, `Packet sent to the ${familyDisplay}.`),
    });

  const renderActions = () => {
    // A year opened by the checklist invite has none of the old flow's actions;
    // once enrolled, the billing page is the way on.
    if (isNewStyle(enrollment)) {
      return enrollment.access === 'ENROLLED' ? (
        <FooterActions hint="Enrolled for the year." primary={{ label: 'Open billing', to: `${familyPath}/billing` }} />
      ) : null;
    }
    if (checklistEnrollmentOn && !['NEEDS_REVIEW', 'APPROVED', 'PACKET_SUBMITTED', 'PACKET_COMPLETED'].includes(enrollment.state)) {
      return null;
    }
    if (checklistEnrollmentOn && (enrollment.state === 'NEEDS_REVIEW' || enrollment.state === 'APPROVED')) {
      return (
        <FooterActions
          hint="This school enrolls families with the checklist. Declining closes the year and sends nothing."
          primary={{ label: 'Decline', onClick: () => setConfirmation('decline'), disabled: busy }}
        />
      );
    }
    switch (enrollment.state) {
      case 'DRAFT':
        return (
          <FooterActions
            hint={enrollment.canQueueForInvite ? 'Queued families go out with the next invite batch.' : 'Add a primary contact email and at least one student first.'}
            primary={{ label: 'Queue for invite', onClick: handleQueue, disabled: busy || !enrollment.canQueueForInvite }}
          />
        );
      case 'READY_TO_INVITE':
        return (
          <FooterActions
            hint={enrollment.canSendInvite ? 'Emails the link to the primary contact.' : 'The enrollment window is closed — open it above to send.'}
            secondary={[{ label: 'Unqueue', onClick: handleUnqueue }]}
            primary={{ label: sendingInvite ? 'Sending...' : 'Send invite', onClick: handleSendInvite, disabled: busy || !enrollment.canSendInvite }}
          />
        );
      case 'INVITED':
      case 'IN_PROGRESS':
        return (
          <FooterActions
            hint="Resending emails the same link again."
            secondary={[{ label: 'Revoke invite', onClick: () => setConfirmation('revoke_invite'), destructive: true }]}
            primary={{ label: resending ? 'Sending...' : 'Resend invite', onClick: handleResendInvite, disabled: busy }}
          />
        );
      case 'NEEDS_REVIEW':
        return (
          <FooterActions
            hint={`Accepts ${acceptingCount} of ${students.length} and drafts invoices. You send the packet next.`}
            secondary={[
              { label: 'Decline', onClick: () => setConfirmation('decline'), destructive: true },
              { label: 'Send back to family', onClick: () => setConfirmation('send_back') },
            ]}
            primary={{ label: completing ? 'Completing...' : 'Complete review', onClick: handleCompleteReview, disabled: busy || students.length === 0 }}
          />
        );
      case 'APPROVED':
        return (
          <FooterActions
            hint="Emails the packet to sign and pay. Draft invoices post when it is signed."
            secondary={[{ label: 'Reopen review', onClick: () => setConfirmation('undo_review') }]}
            primary={{ label: sendingPacket ? 'Sending...' : 'Send packet', onClick: handleSendPacket, disabled: busy }}
          />
        );
      case 'PACKET_SENT':
      case 'PACKET_VIEWED':
        return (
          <FooterActions
            hint="The same link stays valid; copy it to send it another way."
            secondary={[{ label: 'Revoke packet', onClick: () => setConfirmation('revoke_packet'), destructive: true }]}
            primary={{ label: copied ? 'Copied' : 'Copy packet link', onClick: copyLink, disabled: busy, icon: copied ? Check : Copy }}
          />
        );
      case 'PACKET_SUBMITTED':
        return (
          <FooterActions
            hint="Signed — the enrollment completes on its own when payment clears."
            primary={{ label: 'Open billing', to: `${familyPath}/billing` }}
          />
        );
      case 'PACKET_COMPLETED':
        return (
          <FooterActions
            hint={invoices.overdueCount > 0 ? `${formatMoney(invoices.overdueCents)} is overdue.` : 'Enrolled for the year.'}
            primary={{ label: 'Open billing', to: `${familyPath}/billing` }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-foreground/10" onClick={onClose} aria-hidden="true" />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={`${family.name} enrollment`}
        className="fixed inset-y-0 right-0 z-50 flex w-full max-w-[480px] flex-col border-l bg-background shadow-xl"
      >
        <div className="flex items-start justify-between gap-3 px-6 pb-4 pt-5">
          <div className="min-w-0 space-y-1.5">
            <div className="flex items-center gap-2.5">
              <h2 className="truncate text-xl font-semibold">{family.name}</h2>
              <Badge variant="outline">{KIND_LABELS[enrollment.enrollmentKind] ?? enrollment.enrollmentKind}</Badge>
            </div>
            <p className="text-sm text-muted-foreground">{status.text}</p>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <Button asChild variant="ghost" size="sm" className="text-xs">
              <Link to={`${familyPath}/enrollment`}>
                Open family
                <ExternalLink className="h-3.5 w-3.5" />
              </Link>
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose} aria-label="Close">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className="relative flex px-6 pb-4">
          <div className="absolute left-6 right-6 top-[10px] h-0.5 bg-border" />
          {phasesFor(checklistEnrollmentOn).filter((p) => p.key !== 'closed').map((phase, index, all) => {
            const currentIndex = all.findIndex((p) => p.key === currentPhase);
            const state = currentPhase === 'closed' ? 'skipped' : index < currentIndex ? 'done' : index === currentIndex ? 'current' : 'todo';
            return (
              <div key={phase.key} className={`relative flex flex-1 flex-col items-center gap-1.5 text-[11px] ${state === 'current' ? 'font-semibold text-foreground' : 'text-muted-foreground'}`}>
                <span
                  className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${
                    state === 'done' ? 'border-primary bg-primary text-primary-foreground'
                      : state === 'current' ? 'border-primary bg-background'
                        : 'border-border bg-background'
                  }`}
                >
                  {state === 'done' && <Check className="h-3 w-3" />}
                  {state === 'current' && <span className="h-2 w-2 rounded-full bg-primary" />}
                </span>
                {phase.label}
              </div>
            );
          })}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {currentPhase === 'closed' && (
            <div className="mx-6 mb-4 rounded-md border bg-muted px-3 py-2.5 text-sm">This family declined for {schoolYear.name}.</div>
          )}

          {enrollment.state === 'NEEDS_REVIEW' && (
            <div className="mx-6 mb-4 flex gap-2.5 rounded-md border border-orange-200 bg-orange-50 px-3 py-2.5 text-sm text-orange-900">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-orange-600" />
              <div>
                <span className="font-semibold">Why this needs review: </span>
                {reviewReasons.length > 0
                  ? `${reviewReasons.join('; ').toLowerCase()}.`
                  : 'it was marked for a manual look.'}
              </div>
            </div>
          )}

          <Section title="Students">
            {activeStudents.length === 0 && students.length === 0 && (
              <p className="text-sm text-muted-foreground">No students on this enrollment yet.</p>
            )}
            <div className="space-y-2">
              {students.map((se) => (
                <div key={se.id} className="flex items-center justify-between gap-3 rounded-md border px-3 py-2.5">
                  <div className="min-w-0">
                    <p className="truncate font-medium">{se.person.firstName} {se.person.lastName}</p>
                    <p className="text-xs text-muted-foreground">
                      {se.enrollmentKind === 'RETURNING_STUDENT' ? 'Returning' : 'New student'}
                      {se.gradeLevel ? ` · ${se.gradeLevel}` : ''}
                    </p>
                  </div>
                  {enrollment.state === 'NEEDS_REVIEW' ? (
                    <Select value={dispositions[se.id] ?? 'accept'} onValueChange={(value) => setDispositions((prev) => ({ ...prev, [se.id]: value }))}>
                      <SelectTrigger className="h-8 w-36" aria-label={`Decision for ${se.person.firstName}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DISPOSITIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <EnrollmentStatusBadge state={se.state} />
                  )}
                </div>
              ))}
            </div>
          </Section>

          <Section title="Tuition and payment">
            {invoices.count === 0 ? (
              <p className="text-sm text-muted-foreground">
                {/* With the checklist on there is no review to draft them. */}
                {!checklistEnrollmentOn && (currentPhase === 'setup' || currentPhase === 'open' || currentPhase === 'in_review')
                  ? 'Invoices are drafted when the review is completed.'
                  : 'No invoices for this enrollment.'}
              </p>
            ) : (
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span>{PAYMENT_SCHEDULE_LABELS[enrollment.paymentScheduleType ?? ''] ?? 'Invoices'} · {invoices.count} {invoices.count === 1 ? 'invoice' : 'invoices'}</span>
                  <span className="font-medium">{formatMoney(invoices.totalCents)}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>Paid</span><span>{formatMoney(invoices.paidCents)}</span>
                </div>
                {invoices.openCents > 0 && (
                  <div className={`flex justify-between ${invoices.overdueCount > 0 ? 'text-red-600' : 'text-muted-foreground'}`}>
                    <span>{invoices.overdueCount > 0 ? `Overdue (${invoices.overdueCount})` : 'Open'}</span>
                    <span>{formatMoney(invoices.overdueCount > 0 ? invoices.overdueCents : invoices.openCents)}</span>
                  </div>
                )}
                {invoices.draftCount > 0 && (
                  <p className="pt-1 text-xs text-muted-foreground">{invoices.draftCount} draft {invoices.draftCount === 1 ? 'invoice posts' : 'invoices post'} when the packet is signed.</p>
                )}
              </div>
            )}
          </Section>

          <Section title="Guardians">
            {guardians.length === 0 && <p className="text-sm text-muted-foreground">No guardians on file.</p>}
            <div className="space-y-1.5 text-sm">
              {guardians.map((guardian) => (
                <div key={guardian.id} className="flex items-baseline justify-between gap-3">
                  <span className="truncate">
                    <span className="font-medium">{guardian.person.firstName} {guardian.person.lastName}</span>
                    {guardian.isPrimary && <span className="text-muted-foreground"> · primary</span>}
                  </span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {[guardian.person.email, guardian.person.phone].filter(Boolean).join(' · ') || '—'}
                  </span>
                </div>
              ))}
            </div>
          </Section>

          <Section title="Activity">
            <div className="space-y-1.5 text-[13px]">
              {activity.map((entry) => (
                <div key={`${entry.label}-${entry.at}`} className="flex gap-3">
                  <span className="w-14 shrink-0 text-muted-foreground">{formatShortDate(entry.at)}</span>
                  <span>{entry.label}</span>
                </div>
              ))}
              {enrollment.inviteToken && !isNewStyle(enrollment) && currentPhase !== 'complete' && currentPhase !== 'closed' && (
                <div className="flex gap-3 pt-1">
                  <span className="w-14 shrink-0 text-muted-foreground">Link</span>
                  <a href={portalPath} target="_blank" rel="noreferrer" className="truncate font-medium hover:underline">
                    {portalPath}
                  </a>
                </div>
              )}
            </div>
          </Section>
        </div>

        {renderActions()}
      </aside>

      <AlertDialog open={confirmation !== null} onOpenChange={(isOpen) => { if (!isOpen) setConfirmation(null); }}>
        {confirmation && (
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{CONFIRMATIONS[confirmation].title}</AlertDialogTitle>
              <AlertDialogDescription>{CONFIRMATIONS[confirmation].body}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={runConfirmed}
                className={CONFIRMATIONS[confirmation].destructive ? 'bg-destructive text-destructive-foreground hover:bg-destructive/90' : undefined}
              >
                {CONFIRMATIONS[confirmation].action}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        )}
      </AlertDialog>
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2 border-t px-6 py-4">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h3>
      {children}
    </section>
  );
}

interface FooterAction {
  label: string;
  onClick?: () => void;
  to?: string;
  disabled?: boolean;
  destructive?: boolean;
  icon?: typeof Copy;
}

function FooterActions({ hint, primary, secondary = [] }: { hint: string; primary: FooterAction; secondary?: FooterAction[] }) {
  const Icon = primary.icon;
  return (
    <div className="space-y-2 border-t bg-muted/50 px-6 pb-5 pt-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex gap-2">
          {secondary.map((action) => (
            <Button
              key={action.label}
              variant="outline"
              size="sm"
              onClick={action.onClick}
              disabled={action.disabled}
              className={action.destructive ? 'text-destructive hover:text-destructive' : undefined}
            >
              {action.label}
            </Button>
          ))}
        </div>
        {primary.to ? (
          <Button asChild>
            <Link to={primary.to}>{primary.label}</Link>
          </Button>
        ) : (
          <Button onClick={primary.onClick} disabled={primary.disabled}>
            {Icon && <Icon className="h-4 w-4" />}
            {primary.label}
          </Button>
        )}
      </div>
      <p className="text-right text-xs text-muted-foreground">{hint}</p>
    </div>
  );
}

export default FamilyEnrollmentDrawer;
